<template>
  <div>
    <LayoutDashboard>
      <transition name="market-filters-modal">
        <FilterProductModal v-if="goodsFiltersOpened" :hide-filters="['location']" @submit="filter" />
      </transition>

      <div v-show="!goodsFiltersOpened" class="market">
        <DashboardTitle class="market__title" :title="$t('market.title')" />

        <Search
          v-model="searchTerm"
          class="market__search"
          @submit="searchTerm.length && $router.push({ name: 'marketSearch', query: { q: searchTerm } })"
        />

        <ProductGroups class="market__groups" />

        <section class="market__slider">
          <div ref="promoSlider" class="swiper market-slider fade-in">
            <div class="swiper-pagination"></div>

            <button class="button-reset market-slider__prev">
              <ChevronLeft :size="20" />
            </button>

            <button class="button-reset market-slider__next">
              <ChevronRight :size="20" />
            </button>

            <div class="swiper-wrapper">
              <div class="swiper-slide market-slider__slide">
                <img src="@/assets/images/market/slider/payment-delay.webp" alt="Отсрочка платежа" />
              </div>

              <div class="swiper-slide market-slider__slide">
                <img
                  src="@/assets/images/market/slider/loyality.webp"
                  alt="Персональная система лояльности для покупателя"
                />
              </div>

              <div class="swiper-slide market-slider__slide">
                <img
                  src="@/assets/images/market/slider/download-app.webp"
                  alt="База всегда под рукой, скачайте наше мобильное приложение"
                />
              </div>

              <div class="swiper-slide market-slider__slide">
                <img
                  src="@/assets/images/market/slider/delivery.webp"
                  alt="Доставка продуктов питания по всей России"
                />
              </div>
            </div>
          </div>
        </section>

        <section class="market__discounts">
          <ProductsOnSale />
        </section>
      </div>
    </LayoutDashboard>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { Metrics } from '@/utils/metrics'
import globalFiltersFuncs from '@/mixins/globalFiltersFuncs'
import DashboardTitle from '@/components/common/DashboardTitle.vue'
import ProductGroups from '@/components/common/ProductGroups.vue'
import ProductsOnSale from '@/components/market/ProductsOnSale.vue'
import Swiper, { Navigation, Pagination as SwiperPagination, Autoplay } from 'swiper'
import 'swiper/swiper.css'
import { ChevronLeft, ChevronRight } from 'lucide-vue'
import Search from '@/components/common/Search.vue'

export default {
  name: 'Market',

  metaInfo() {
    return {
      title: 'Оптово-розничный рынок продуктов нового поколения БАZA',
      meta: [
        {
          vmid: 'description',
          name: 'description',
          content:
            'БАZA — оптово-розничная платформа свежих продуктов нового поколения. Продажа от одной коробки напрямую от производителей. Прямые поставки, прозрачные цены, товары для бизнеса и частных клиентов без посредников.',
        },
        {
          vmid: 'og:title',
          name: 'og:title',
          content: 'Оптово-розничный рынок продуктов нового поколения БАZA',
        },
        {
          vmid: 'og:description',
          name: 'og:description',
          content:
            'БАZA — оптово-розничная платформа свежих продуктов нового поколения. Продажа от одной коробки напрямую от производителей. Прямые поставки, прозрачные цены, товары для бизнеса и частных клиентов без посредников.',
        },
      ],
    }
  },

  components: {
    DashboardTitle,
    ProductGroups,
    ProductsOnSale,
    ChevronLeft,
    ChevronRight,
    Search,
    FilterProductModal: () => import('@/components/modals/FilterProductModal'),
  },
  mixins: [globalFiltersFuncs],

  data() {
    return {
      orderBy: null,
      searchString: '',
      abortController: new AbortController(),
      isLoading: false,
      withDiscount: false,
      scrollTracked: [25, 50, 75, 100],
      reached: new Set(),
      searchTerm: '',
    }
  },

  computed: {
    ...mapState('product', ['products']),
    ...mapState('profileProduct', ['sellerProducts', 'totalPages']),
    ...mapGetters('auth', ['loggedIn']),
    ...mapGetters('filtration', [
      'productsSelected',
      'productCategoriesSelected',
      'productGroupsSelected',
      'sortsSelected',
      'countriesSelected',
      'citiesSelected',
      'warehousesSelected',
    ]),
    ...mapGetters(['goodsFiltersOpened']),
  },

  watch: {},

  async created() {
    this.clearAllFilters()
  },

  async mounted() {
    this.$store.dispatch('misc/loadWeightUnits')

    new Swiper(this.$refs.promoSlider, {
      modules: [Navigation, SwiperPagination, Autoplay],
      loop: true,
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 12,
      autoplay: {
        delay: 5000,
        pauseOnMouseEnter: true,
      },
      navigation: {
        prevEl: '.market-slider__prev',
        nextEl: '.market-slider__next',
      },
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
      breakpoints: {
        992: {
          slidesPerView: 2,
          slidesPerGroup: 2,
        },
      },
    })

    window.addEventListener('scroll', this.handleScroll)
  },

  beforeDestroy() {
    this.setGoodsFiltersOpened(false)
    this.abortController.abort()
    window.removeEventListener('scroll', this.handleScroll)
  },

  methods: {
    ...mapActions('filtration', [
      'setProductCategoriesSelected',
      'setProductGroupsSelected',
      'setProductsSelected',
      'setProductSortsSelected',
      'setCountriesSelected',
      'setCitiesSelected',
    ]),
    ...mapActions(['setGoodsFiltersOpened']),

    handleScroll() {
      const scrollPosition = window.scrollY + window.innerHeight
      const pageHeight = document.documentElement.scrollHeight
      const scrollPercent = Math.round((scrollPosition / pageHeight) * 100)

      this.scrollTracked.forEach(percent => {
        if (scrollPercent >= percent && !this.reached.has(percent)) {
          this.reached.add(percent)
          Metrics.reportEvent('scroll_market', { percent })
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/functions';
@import '@/assets/scss/config';
@import '@/assets/scss/mixins';
@import '~bootstrap/scss/variables';
@import '~bootstrap/scss/mixins';
@import '@/assets/scss/silence/dashboard-main';
// Aditional blocks
@import '@/assets/scss/blocks/product-cards-list';
@import '@/assets/scss/animations.scss';
@import '@/assets/scss/transitions.scss';

.market {
  position: relative;
  @extend %dashboard-main;
  grid-template-columns: 1fr 1fr;
  grid-auto-rows: max-content;
  grid-template-areas:
    'title title'
    'search search'
    'groups groups'
    'slider slider'
    'discounts discounts';

  padding: 16px 10px 110px;

  @include media-breakpoint-up(sm) {
    padding: 24px 16px 110px;
    grid-row-gap: 32px;
  }

  .loading-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 11;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  // Elements
  .market__title {
    grid-area: title;
    margin: 0;
  }

  .market__groups {
    grid-area: groups;
  }

  .market__discounts {
    grid-area: discounts;
  }

  .market__slider {
    grid-area: slider;
  }

  .market__pagination {
    grid-area: pagination;
  }

  .market__search {
    grid-area: search;
  }

  .market__fast-filters {
    grid-area: fast-filters;
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;

    @include media-breakpoint-down(sm) {
      display: none;
    }
  }
  .market__fast-filters-item {
    font-size: 14px;
    line-height: 18px;
    color: $DGray;
    text-decoration: underline;
    cursor: pointer;

    & + .market__fast-filters-item {
      margin-left: 28px;
    }
  }

  .market__list {
    grid-area: list;
  }
}

.market__sort-n-show {
  grid-area: sort-n-order;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
}

.market-sort-n-order {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  flex: 1 1 calc(100% - 50px);

  @include media-breakpoint-up(sm) {
    justify-content: flex-end;
  }

  &__item {
    position: relative;
    cursor: pointer;

    &:not(:last-child) {
      margin-right: $base-grid * 2;

      @include media-breakpoint-up(sm) {
        margin-right: $base-grid * 3;
      }
    }

    span {
      font-size: 14px;
      line-height: 18px;
    }
  }

  &__button {
    display: inline-flex;
    align-items: center;
    color: $DGray;
    background-color: transparent;
    border: none;
    padding: 0;
    cursor: pointer;

    &--active {
      span {
        color: $GreenM;
      }
    }
  }
}

.market-slider {
  position: relative;
  display: none;

  &.swiper-initialized {
    display: block;
  }

  &__slide {
    border-radius: 16px;
    overflow: hidden;

    > img {
      display: block;
      width: 100%;
      height: 100%;
    }
  }

  &__prev,
  &__next {
    position: absolute;
    width: 24px;
    height: 24px;
    top: 50%;
    transform: translateY(-50%);
    border-radius: 50%;
    background-color: rgba(0, 0, 0, 0.15);
    z-index: 10;
    color: $White;
    display: flex;
    align-items: center;
    justify-content: center;

    @include media-breakpoint-up(sm) {
      width: 32px;
      height: 32px;
    }
  }

  &__prev {
    left: 8px;

    @include media-breakpoint-up(sm) {
      left: 12px;
    }
  }

  &__next {
    right: 12px;

    @include media-breakpoint-up(sm) {
      right: 12px;
    }
  }
}
</style>
